import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import TrashIcon from "../../../../../assets/svg/trash-icon";
import type { FilterFieldOption } from "../../HelpDesk/components/CreateView/utils/constants";
import { FilterDropdown } from "../../HelpDesk/components/CreateView/components/FilterConditionRow";
import MultiSelectSearchableDropdown from "../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";
import {
  useGetLeadCountriesQuery,
  useGetLeadCitiesQuery,
  useGetLeadUniversitiesQuery,
  useGetAllLeadStagesQuery,
  useGetCrmDistinctValuesQuery,
  CRM_ATTRIBUTION_FIELDS,
  type CrmAttributionField,
} from "../../../../../redux/services/crmApi";
import {
  useGetLeadDetailTagsQuery,
  useGetListingFiltersQuery,
} from "../../../../../redux/services/communicationApi";
import { useGetPortfoliosQuery } from "../../../../../redux/services/pmsApi";
import { useGetPropertiesListQuery } from "../../../../../redux/services/playgroundApi";
import { useGetCampaignsQuery } from "../../../../../redux/services/campaignApi";
import {
  useGetDealStagesQuery,
  useGetDealDistinctCitiesQuery,
  useGetDealDistinctAvailabilityTitlesQuery,
  useGetDealDistinctAcademicYearsQuery,
  useGetDealDistinctRoomTypesQuery,
} from "../../../../../redux/services/dealsApi";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import type {
  FilterConditionUI,
  FilterGroupUI,
} from "../utlis/crmViewConstants";
import {
  getCrmViewFilterFieldOptions,
  CRM_VALUE_OPTIONS_MAP,
  CRM_SINGLE_VALUE_FIELDS,
  CRM_FIELD_DEPENDENCIES,
  DURATION_OPTIONS,
  getCrmOperatorsForField,
  getSelectedValuesForField,
} from "../utlis/crmViewConstants";
import { useGetAllAgentsQuery } from "../../../../../redux/services/userManagementApi";
import { getClientDomain } from "../../../../../utils/functions/client-code";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { getLeadTagDisplayName } from "../../../../../utils/constants/mappings";
import { useTenantTypeOptions } from "../../../../../hooks/useTenantTypeOptions";
import {
  EXISTS_VALUE_OPTIONS,
  FILTER_NUMBER_VALUE_PLACEHOLDERS,
  HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS,
} from "../../HelpDesk/components/CreateView/utils/constants";
import DurationPopper from "../../../../../components/DurationPopper/DurationPopper";
import type { DurationOption } from "../../../../../components/DurationPopper/utils/constants";
import {
  formatFilterBetweenValueDisplay,
  getDurationPopperInitialForFilter,
} from "../../HelpDesk/components/CreateView/utils/functions";
import { convertLocalToUtcIsoString } from "../../../../../utils/functions/date-time";
import SingleDatePicker from "../../../../../components/SingleDatePicker/SingleDatePicker";
import {
  SEGMENT_DEAL_NUMBER_FIELDS,
  SEGMENT_DEAL_DATE_FIELDS,
  SEGMENT_DEAL_FREE_TEXT_FIELDS,
  SEGMENT_DEAL_ARRAY_FIELDS,
  SEGMENT_LEAD_STRING_FIELDS,
} from "../../../../Campaigns/components/Segments/constants/segments.constants";

interface CrmFilterConditionRowProps {
  condition: FilterConditionUI;
  onChange: (updated: FilterConditionUI) => void;
  onDelete: () => void;
  showBackground?: boolean;
  fieldOptions?: FilterFieldOption[];
  filterGroups?: FilterGroupUI[];
  extraOperators?: Record<string, { key: string; label: string }[]>;
  extraValueOptions?: Record<string, { key: string; label: string }[]>;
  extraSingleValueFields?: string[];
  extraFreeTextFields?: string[];
}

export const CrmFilterConditionRow = ({
  condition,
  onChange,
  onDelete,
  showBackground = true,
  fieldOptions: fieldOptionsProp,
  filterGroups = [],
  extraOperators,
  extraValueOptions,
  extraSingleValueFields,
  extraFreeTextFields,
}: CrmFilterConditionRowProps) => {
  const authClientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const authCurrentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  const { options: tenantTypeOpts } = useTenantTypeOptions();
  const leadTypeOptions = useMemo(
    () => tenantTypeOpts.map((opt) => ({ key: opt.value, label: opt.label })),
    [tenantTypeOpts]
  );
  const [autoSelectedKey, setAutoSelectedKey] = useState<string | null>(null);
  const defaultFieldOptions = useMemo(
    () => getCrmViewFilterFieldOptions(authClientCode),
    [authClientCode]
  );
  const fieldOptions = fieldOptionsProp ?? defaultFieldOptions;
  const selectedField = fieldOptions?.find((f) => f?.key === condition?.key);

  const needsCountries =
    condition?.key === "source_country" ||
    condition?.key === "destination_country";
  const needsCities = condition?.key === "destination_city";
  const needsUniversities = condition?.key === "university";
  const needsAgents =
    condition?.key === "assigned_agents" || condition?.key === "deal_assignee";
  const needsTags = condition?.key === "tags";
  const needsProperties =
    condition?.key === "property" || condition?.key === "deal_property_name";
  const needsListingFilters =
    condition?.key === "utm_source" ||
    condition?.key === "utm_medium" ||
    condition?.key === "room_type";
  const needsCampaigns = condition?.key === "utm_workflow";
  const needsInvestors = condition?.key === "investor";
  const needsLeadStages = condition?.key === "current_stage";
  const needsDealStages = condition?.key === "deal_stage";
  const needsDealCities = condition?.key === "deal_city";
  const needsDealAvailabilityTitles =
    condition?.key === "deal_availability_title";
  const needsDealAcademicYears = condition?.key === "deal_academic_year";
  const needsDealRoomTypes = condition?.key === "deal_room_type";
  const needsAttribution = CRM_ATTRIBUTION_FIELDS.includes(
    condition?.key as CrmAttributionField
  );

  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetLeadCountriesQuery(undefined, { skip: !needsCountries });

  const selectedCountryIds = useMemo(
    () => getSelectedValuesForField(filterGroups, "destination_country"),
    [filterGroups]
  );

  const selectedCityIds = useMemo(
    () => getSelectedValuesForField(filterGroups, "destination_city"),
    [filterGroups]
  );

  const { data: citiesData, isLoading: isCitiesLoading } =
    useGetLeadCitiesQuery(selectedCountryIds, {
      skip: !needsCities || selectedCountryIds.length === 0,
    });

  const { data: universitiesData, isLoading: isUniversitiesLoading } =
    useGetLeadUniversitiesQuery(selectedCityIds, {
      skip: !needsUniversities || selectedCityIds.length === 0,
    });

  const { data: agentsData, isLoading: isAgentsLoading } = useGetAllAgentsQuery(
    undefined,
    { skip: !needsAgents }
  );

  const { data: tagsData, isLoading: isTagsLoading } =
    useGetLeadDetailTagsQuery(undefined, { skip: !needsTags });

  const { data: propertiesData, isLoading: isPropertiesLoading } =
    useGetPropertiesListQuery(
      { managerId: authCurrentClientCode ?? "", page: 1, pageSize: 1000 },
      { skip: !needsProperties || !authCurrentClientCode }
    );

  const utmQueryArgs = useMemo(() => {
    const now = new Date();
    const past = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    return {
      start_date: past.toISOString(),
      end_date: now.toISOString(),
      chat_type: "leads_created",
    };
  }, []);

  const { data: listingFiltersData, isLoading: isListingFiltersLoading } =
    useGetListingFiltersQuery(utmQueryArgs, { skip: !needsListingFilters });

  const utmSourceOptions = useMemo(() => {
    if (!listingFiltersData?.utm_sources) return [];
    return listingFiltersData.utm_sources.map((s: string) => ({
      key: s,
      label: s,
    }));
  }, [listingFiltersData]);

  const utmMediumOptions = useMemo(() => {
    if (!listingFiltersData?.utm_mediums) return [];
    return listingFiltersData.utm_mediums.map((s: string) => ({
      key: s,
      label: s,
    }));
  }, [listingFiltersData]);

  const roomTypeOptions = useMemo(() => {
    if (!listingFiltersData?.room_types) return [];
    return listingFiltersData.room_types.map((rt: string) => ({
      key: rt,
      label: rt.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    }));
  }, [listingFiltersData]);

  const campaignQueryArgs = useMemo(
    () => ({
      search_query: "",
      skip: 0,
      limit: 500,
      campaign_status: ["completed"],
    }),
    []
  );

  const { data: campaignsData, isLoading: isCampaignsLoading } =
    useGetCampaignsQuery(campaignQueryArgs, { skip: !needsCampaigns });

  const campaignOptions = useMemo(() => {
    if (!campaignsData?.campaigns) return [];
    return campaignsData.campaigns.map((c: { id: string; name: string }) => ({
      key: c.id,
      label: c.name,
    }));
  }, [campaignsData]);

  const clientCode = useMemo(() => getClientDomain(), []);
  const { data: portfoliosData, isLoading: isInvestorsLoading } =
    useGetPortfoliosQuery(clientCode, {
      skip: !needsInvestors || !clientCode,
    });

  const investorOptions = useMemo(() => {
    const investors = portfoliosData?.res;
    if (!investors?.length) return [];
    return investors.map((inv: { id: number; name: string; slug: string }) => ({
      key: inv.slug,
      label: inv.name,
    }));
  }, [portfoliosData]);

  const { data: leadStagesData, isLoading: isLeadStagesLoading } =
    useGetAllLeadStagesQuery(undefined, { skip: !needsLeadStages });

  const leadStageOptions = useMemo(() => {
    if (!leadStagesData) return [];
    return (
      leadStagesData as { name: string; code: string; base_color?: string }[]
    ).map((s) => ({
      key: s?.code,
      label: s?.name,
      color: s?.base_color,
    }));
  }, [leadStagesData]);

  const { data: dealStagesData, isLoading: isDealStagesLoading } =
    useGetDealStagesQuery(undefined, { skip: !needsDealStages });

  const dealStageOptions = useMemo(() => {
    if (!Array.isArray(dealStagesData)) return [];
    return dealStagesData
      .map((s) => {
        const stageKey = s?.stage_key ?? s?.key ?? "";
        const stageTitle = s?.stage_title ?? s?.title ?? stageKey;
        return {
          key: stageKey,
          label: stageTitle,
          color: s?.secondary_hex_code,
        };
      })
      .filter((opt) => opt.key);
  }, [dealStagesData]);

  const { data: dealCitiesData, isLoading: isDealCitiesLoading } =
    useGetDealDistinctCitiesQuery(undefined, { skip: !needsDealCities });

  const dealCityOptions = useMemo(() => {
    const values = dealCitiesData?.values ?? [];
    return values.map((v) => ({ key: v, label: v }));
  }, [dealCitiesData]);

  const {
    data: dealAvailabilityTitlesData,
    isLoading: isDealAvailabilityTitlesLoading,
  } = useGetDealDistinctAvailabilityTitlesQuery(undefined, {
    skip: !needsDealAvailabilityTitles,
  });

  const dealAvailabilityTitleOptions = useMemo(() => {
    const values = dealAvailabilityTitlesData?.values ?? [];
    return values.map((v) => ({ key: v, label: v }));
  }, [dealAvailabilityTitlesData]);

  const { data: dealAcademicYearsData, isLoading: isDealAcademicYearsLoading } =
    useGetDealDistinctAcademicYearsQuery(undefined, {
      skip: !needsDealAcademicYears,
    });

  const dealAcademicYearOptions = useMemo(() => {
    const values = dealAcademicYearsData?.values ?? [];
    return values.map((v) => ({ key: v, label: v }));
  }, [dealAcademicYearsData]);

  const { data: dealRoomTypesData, isLoading: isDealRoomTypesLoading } =
    useGetDealDistinctRoomTypesQuery(undefined, {
      skip: !needsDealRoomTypes,
    });

  const dealRoomTypeOptions = useMemo(() => {
    const values = dealRoomTypesData?.values ?? [];
    return values.map((v) => ({ key: v, label: v }));
  }, [dealRoomTypesData]);

  const { data: attributionData, isLoading: isAttributionLoading } =
    useGetCrmDistinctValuesQuery(condition?.key as CrmAttributionField, {
      skip: !needsAttribution,
      refetchOnMountOrArgChange: true,
    });

  const attributionOptions = useMemo(() => {
    const values = attributionData?.values ?? [];
    return values.map((v) => ({ key: v, label: v }));
  }, [attributionData]);

  const countryOptions = useMemo(() => {
    if (!countriesData) return [];
    return (
      countriesData as { id: number; name: string; alpha_2: string }[]
    ).map((c) => ({
      key: String(c.id),
      label: c.name,
    }));
  }, [countriesData]);

  const sourceCountryOptions = useMemo(() => {
    if (!countriesData) return [];
    return (
      countriesData as { id: number; name: string; alpha_2: string }[]
    )?.map((c) => ({
      key: c?.alpha_2,
      label: c?.name,
    }));
  }, [countriesData]);

  const cityOptions = useMemo(() => {
    if (!citiesData) return [];
    return (citiesData as { id: number; name: string }[]).map((c) => ({
      key: String(c.id),
      label: c.name,
    }));
  }, [citiesData]);

  const universityOptions = useMemo(() => {
    if (!universitiesData) return [];
    return (universitiesData as { id: number; name: string }[]).map((u) => ({
      key: String(u.id),
      label: u.name,
    }));
  }, [universitiesData]);

  const agentOptions = useMemo(() => {
    if (!agentsData?.items) return [];
    return agentsData.items.map(
      (u: { _id: string; name: string; email: string }) => ({
        key: u._id || u.email,
        label: u.name || u.email,
      })
    );
  }, [agentsData]);

  const tagOptions = useMemo(() => {
    if (!tagsData) return [];
    return (tagsData as { code: string; name: string }[]).map((t) => ({
      key: t.code,
      label: getLeadTagDisplayName(t.name, authClientCode),
    }));
  }, [tagsData, authClientCode]);

  const propertyOptions = useMemo(() => {
    return (
      propertiesData?.res?.map((p: { id: number; name: string }) => ({
        key: String(p.id),
        label: p.name,
      })) ?? []
    );
  }, [propertiesData]);

  // Deal-side property filter (`deal_property_name`) keys by the property NAME
  // because the BE field stores the name string, not the property id.
  const dealPropertyOptions = useMemo(() => {
    return (
      propertiesData?.res?.map((p: { id: number; name: string }) => ({
        key: p.name,
        label: p.name,
      })) ?? []
    );
  }, [propertiesData]);

  const valueOptions = useMemo(() => {
    if (condition?.key === "room_type") return roomTypeOptions;
    if (condition?.key === "deal_room_type") return dealRoomTypeOptions;
    if (condition?.key === "deal_assignee") return agentOptions;
    if (condition?.key === "deal_property_name") return dealPropertyOptions;
    if (condition?.key === "deal_stage") return dealStageOptions;
    if (condition?.key === "deal_city") return dealCityOptions;
    if (condition?.key === "deal_availability_title")
      return dealAvailabilityTitleOptions;
    if (condition?.key === "deal_academic_year") return dealAcademicYearOptions;
    const extraOpts = extraValueOptions?.[condition?.key];
    if (extraOpts) return extraOpts;
    if (condition?.key === "lead_type") return leadTypeOptions;
    const staticOpts = CRM_VALUE_OPTIONS_MAP[condition?.key];
    if (staticOpts) return staticOpts;
    if (condition?.key === "duration") return DURATION_OPTIONS;
    if (condition?.key === "source_country") return sourceCountryOptions;
    if (condition?.key === "destination_country") return countryOptions;
    if (condition?.key === "destination_city") return cityOptions;
    if (condition?.key === "university") return universityOptions;
    if (condition?.key === "assigned_agents") return agentOptions;
    if (condition?.key === "tags") return tagOptions;
    if (condition?.key === "property") return propertyOptions;
    if (condition?.key === "utm_source") return utmSourceOptions;
    if (condition?.key === "utm_medium") return utmMediumOptions;
    if (condition?.key === "utm_workflow") return campaignOptions;
    if (condition?.key === "investor") return investorOptions;
    if (condition?.key === "current_stage") return leadStageOptions;
    if (needsAttribution) return attributionOptions;
    return [];
  }, [
    condition?.key,
    extraValueOptions,
    needsAttribution,
    attributionOptions,
    roomTypeOptions,
    countryOptions,
    sourceCountryOptions,
    cityOptions,
    universityOptions,
    agentOptions,
    tagOptions,
    propertyOptions,
    dealPropertyOptions,
    utmSourceOptions,
    utmMediumOptions,
    campaignOptions,
    investorOptions,
    leadStageOptions,
    leadTypeOptions,
    dealStageOptions,
    dealCityOptions,
    dealAvailabilityTitleOptions,
    dealAcademicYearOptions,
    dealRoomTypeOptions,
  ]);

  const operatorOptions = useMemo(() => {
    const extraOps = extraOperators?.[condition?.key ?? ""];
    if (extraOps) return extraOps;
    return getCrmOperatorsForField(condition?.key ?? "");
  }, [condition?.key, extraOperators]);

  const isValueLoading = useMemo(() => {
    if (!condition?.key) return false;
    if (needsCountries && isCountriesLoading) return true;
    if (needsCities && isCitiesLoading) return true;
    if (needsUniversities && isUniversitiesLoading) return true;
    if (needsAgents && isAgentsLoading) return true;
    if (needsTags && isTagsLoading) return true;
    if (needsProperties && isPropertiesLoading) return true;
    if (needsListingFilters && isListingFiltersLoading) return true;
    if (needsCampaigns && isCampaignsLoading) return true;
    if (needsInvestors && isInvestorsLoading) return true;
    if (needsLeadStages && isLeadStagesLoading) return true;
    if (needsDealStages && isDealStagesLoading) return true;
    if (needsDealCities && isDealCitiesLoading) return true;
    if (needsDealAvailabilityTitles && isDealAvailabilityTitlesLoading)
      return true;
    if (needsDealAcademicYears && isDealAcademicYearsLoading) return true;
    if (needsDealRoomTypes && isDealRoomTypesLoading) return true;
    if (needsAttribution && isAttributionLoading) return true;
    return false;
  }, [
    condition?.key,
    needsCountries,
    isCountriesLoading,
    needsCities,
    isCitiesLoading,
    needsUniversities,
    isUniversitiesLoading,
    needsAgents,
    isAgentsLoading,
    needsTags,
    isTagsLoading,
    needsProperties,
    isPropertiesLoading,
    needsListingFilters,
    isListingFiltersLoading,
    needsCampaigns,
    isCampaignsLoading,
    needsInvestors,
    isInvestorsLoading,
    needsLeadStages,
    isLeadStagesLoading,
    needsDealStages,
    isDealStagesLoading,
    needsDealCities,
    isDealCitiesLoading,
    needsDealAvailabilityTitles,
    isDealAvailabilityTitlesLoading,
    needsDealAcademicYears,
    isDealAcademicYearsLoading,
    needsDealRoomTypes,
    isDealRoomTypesLoading,
    needsAttribution,
    isAttributionLoading,
  ]);
  const valueLoadingPlaceholder = selectedField?.label ?? "Value";
  const isDurationField = condition?.key === "duration";
  const isDealNumberField = SEGMENT_DEAL_NUMBER_FIELDS.includes(condition?.key);
  const isDealDateField = SEGMENT_DEAL_DATE_FIELDS.includes(condition?.key);
  const isDealFreeTextField = SEGMENT_DEAL_FREE_TEXT_FIELDS.includes(
    condition?.key
  );
  const isDealArrayField = SEGMENT_DEAL_ARRAY_FIELDS.includes(condition?.key);
  const isBetweenDateOrDuration =
    (isDealDateField || isDurationField) &&
    condition?.comparison_operator === "range";
  const isSingleValueField =
    CRM_SINGLE_VALUE_FIELDS.includes(condition?.key) ||
    (extraSingleValueFields?.includes(condition?.key) ?? false);
  const isMultiValueOperator =
    condition?.comparison_operator === "not_in" ||
    condition?.comparison_operator === "contains_any";
  // Deal array fields: `contains` and `not_contains` also accept multiple
  // values (sent as array under the same operator key).
  const isDealArrayMultiOp =
    isDealArrayField &&
    (condition?.comparison_operator === "contains" ||
      condition?.comparison_operator === "not_contains");
  const isMultiSelectOperator = isMultiValueOperator || isDealArrayMultiOp;
  const isDealArrayAsSingle = isDealArrayField && !isMultiSelectOperator;
  const isLeadStringField = SEGMENT_LEAD_STRING_FIELDS.includes(condition?.key);
  // Lead-side stays single on `contains` / `not_contains` — uses the base flag.
  const isLeadStringAsSingle = isLeadStringField && !isMultiValueOperator;
  // Free-text fields opt in via `extraFreeTextFields` and render a plain text
  // box on `contains` / `not_contains` instead of a dropdown. CRM Leads passes
  // none, so it keeps its dropdowns; only Segment opts specific fields in.
  const isContainsTextInput =
    (extraFreeTextFields?.includes(condition?.key) ?? false) &&
    (condition?.comparison_operator === "contains" ||
      condition?.comparison_operator === "not_contains");

  useEffect(() => {
    if (!condition?.key) return;
    if (isContainsTextInput) return;
    if (isValueLoading) return;
    if (autoSelectedKey === condition?.key) return;
    if ((condition?.value?.length ?? 0) > 0) {
      setAutoSelectedKey(condition?.key);
      return;
    }
    if (valueOptions?.length !== 1) return;
    setAutoSelectedKey(condition?.key);
    onChange({ ...condition, value: [valueOptions[0]?.key] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    condition?.key,
    valueOptions,
    isValueLoading,
    condition?.value?.length,
    autoSelectedKey,
  ]);

  const disabledFields = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [fieldKey, dep] of Object.entries(CRM_FIELD_DEPENDENCIES)) {
      const parentValues = getSelectedValuesForField(
        filterGroups,
        dep.parentField
      );
      if (parentValues.length === 0) {
        map[fieldKey] = dep.tooltip;
      }
    }
    return map;
  }, [filterGroups]);

  const handleFieldChange = (key: string) => {
    if (disabledFields[key]) return;
    const defaultOp =
      extraOperators?.[key]?.[0]?.key ??
      getCrmOperatorsForField(key)[0]?.key ??
      "contains";
    const nextObjectType = key.startsWith("deal_") ? "deal" : "lead";
    onChange({
      ...condition,
      key,
      value: [],
      comparison_operator: defaultOp,
      is_custom_field: false,
      object_type: nextObjectType,
    });
  };

  const handleOperatorChange = (op: string) => {
    const wasExists = condition?.comparison_operator === "exists";
    const isExists = op === "exists";
    const wasRange = condition?.comparison_operator === "range";
    const isRange = op === "range";
    onChange({
      ...condition,
      comparison_operator: op,
      ...(wasExists || isExists || wasRange !== isRange ? { value: [] } : {}),
    });
  };

  const handleSingleValueChange = (val: string) => {
    onChange({ ...condition, value: [val] });
  };

  const handleValueChange = (vals: string[]) => {
    onChange({ ...condition, value: vals });
  };

  const handleNumberInputChange = (val: string, index: number = 0) => {
    const newValue = [...(condition?.value || [])];
    newValue[index] = val;
    onChange({ ...condition, value: newValue });
  };

  const handleTextInputChange = (val: string) => {
    onChange({ ...condition, value: [val] });
  };

  const [betweenDurationOpen, setBetweenDurationOpen] = useState(false);

  useEffect(() => {
    if (!isBetweenDateOrDuration) setBetweenDurationOpen(false);
  }, [isBetweenDateOrDuration]);

  const durationPopperInitial = useMemo(() => {
    const summary = formatFilterBetweenValueDisplay(condition?.value ?? []);
    if (!summary) {
      return {
        initialOption: { id: "custom", label: "Custom" } as DurationOption,
      };
    }
    return getDurationPopperInitialForFilter(condition?.value ?? []);
  }, [condition?.value]);

  const hasBetweenDurationFilterValue = useMemo(
    () => Boolean(formatFilterBetweenValueDisplay(condition?.value ?? [])),
    [condition?.value]
  );

  const isExistsOperator = condition?.comparison_operator === "exists";

  // Parse value[0] as a Date for deal date fields. Legacy preset keys like
  // "last_7_days" from older saved segments are treated as empty so the user
  // sees the placeholder and can pick a real date.
  const dealDatePickerValue = useMemo<Date | null>(() => {
    const raw = condition?.value?.[0];
    if (!raw) return null;
    if (!/^\d{4}-\d{2}-\d{2}(T|$)/.test(raw)) return null;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [condition?.value]);

  const handleDealDateChange = (date: Date | null) => {
    if (!date) {
      onChange({ ...condition, value: [] });
      return;
    }
    const isoDate = convertLocalToUtcIsoString(
      format(date, "yyyy-MM-dd'T'HH:mm:ss")
    );
    onChange({ ...condition, value: [isoDate] });
  };

  return (
    <div
      className={`flex items-center gap-2 h-[48px] ${showBackground ? "bg-hover-effect dark:bg-hover-effect-dk rounded-lg p-2" : ""}`}
    >
      <FilterDropdown
        options={fieldOptions?.map((f) => ({ key: f?.key, label: f?.label }))}
        value={condition?.key}
        onChange={handleFieldChange}
        placeholder="Select filter"
        width="min-w-[150px] max-w-[150px]"
        dropdownClassName="!min-w-[200px]"
        showSearch={fieldOptions && fieldOptions.length > 6}
        searchPlaceholder="Search"
        groupBy={(opt) => {
          const f = fieldOptions?.find((x) => x.key === opt.key);
          return f?.section;
        }}
        groupOrder={["lead", "deal"]}
        renderGroupHeader={(group) => (
          <div
            key={group}
            className="px-3 pt-1.5 pb-0.5 flex flex-col items-start"
          >
            <span className="text-xxs font-semibold uppercase text-dark-gray dark:text-dark-gray-dk">
              {group === "deal"
                ? "Bookings"
                : getClientCRMFlag("useContactLabel")
                  ? "Contacts"
                  : "Leads"}
            </span>
          </div>
        )}
        renderOption={(opt) => {
          const field = fieldOptions?.find((f) => f?.key === opt?.key);
          if (!field) return opt?.label;
          const Icon = field?.icon;
          const tooltipText = disabledFields[opt?.key];
          const content = (
            <div
              className={`flex items-center gap-1.5 w-full ${tooltipText ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Icon className="shrink-0 w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="truncate">{opt?.label}</span>
            </div>
          );
          if (tooltipText) {
            return (
              <Tooltip content={tooltipText} side="right">
                {content}
              </Tooltip>
            );
          }
          return content;
        }}
        renderSelected={(opt) => {
          if (!selectedField) return opt?.label;
          const Icon = selectedField?.icon;
          return (
            <>
              <Icon className="shrink-0 w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="truncate">{opt?.label}</span>
            </>
          );
        }}
      />

      {condition?.key &&
        (operatorOptions?.length === 1 ? (
          <span className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] max-w-[90px] flex items-center justify-center px-2 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk select-none whitespace-nowrap">
            {operatorOptions?.[0]?.label}
          </span>
        ) : (
          <FilterDropdown
            options={operatorOptions}
            value={condition?.comparison_operator}
            onChange={handleOperatorChange}
            placeholder="Operator"
            width="w-[90px]"
            textClassName="font-medium"
          />
        ))}

      {condition?.key &&
        (isExistsOperator ? (
          <FilterDropdown
            options={EXISTS_VALUE_OPTIONS}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder="Select"
            width="min-w-[150px] max-w-[150px]"
          />
        ) : isContainsTextInput ? (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleTextInputChange(e.target.value)}
              placeholder={valueLoadingPlaceholder}
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors"
            />
          </div>
        ) : isBetweenDateOrDuration ? (
          <DurationPopper
            key={`${condition.key}-${condition.value?.join("|") ?? ""}-between`}
            variant="default"
            isOpen={betweenDurationOpen}
            onOpen={() => setBetweenDurationOpen(true)}
            onClose={() => setBetweenDurationOpen(false)}
            applyOnMount={false}
            durationOptions={
              HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS as DurationOption[]
            }
            title="Duration"
            triggerName={valueLoadingPlaceholder}
            triggerAriaLabel={valueLoadingPlaceholder}
            initialDates={durationPopperInitial.initialDates}
            initialOption={durationPopperInitial.initialOption}
            onApply={(from, to, selectedDurationOption) => {
              const optionId = selectedDurationOption?.id ?? "custom";
              onChange({
                ...condition,
                value: [
                  convertLocalToUtcIsoString(
                    format(from, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  convertLocalToUtcIsoString(
                    format(to, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  optionId,
                ],
              });
            }}
            portalHorizontalAlign="end"
            className="flex-1 min-w-0 self-center"
            triggerClassName="!rounded-lg !h-[32px] !max-h-[32px] !min-h-[32px] w-full !bg-pure-white dark:!bg-pure-white-dk !backdrop-blur-none border border-solid border-border-gray dark:border-border-gray-dk !shadow-none px-0"
            triggerIconClassName="!w-[14px] !h-[14px] !shrink-0 !text-verba-indigo dark:!text-verba-indigo-dk !transition-transform"
            selectedOptionContainerClassName="!h-full !min-h-0 !max-w-[150px] min-w-0 !justify-between !px-2 !py-0 items-center"
            triggerTextClassName={
              hasBetweenDurationFilterValue
                ? "text-md font-medium !text-verba-indigo dark:!text-verba-indigo-dk"
                : "text-md font-medium !text-gray-placeholder dark:!text-gray-placeholder-dk"
            }
          />
        ) : isDealDateField ? (
          <div className="min-w-[150px] max-w-[150px]">
            <SingleDatePicker
              value={dealDatePickerValue}
              onChange={handleDealDateChange}
              placeholder={valueLoadingPlaceholder}
              containerClassName="w-full"
              position="left"
              inputClassName="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] px-2 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
            />
          </div>
        ) : isDealNumberField ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              type="number"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleNumberInputChange(e.target.value, 0)}
              onKeyDown={(e) => {
                if (["e", "E", "+"].includes(e.key)) e.preventDefault();
              }}
              placeholder={
                condition?.comparison_operator === "range"
                  ? FILTER_NUMBER_VALUE_PLACEHOLDERS.min
                  : FILTER_NUMBER_VALUE_PLACEHOLDERS.single
              }
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            {condition?.comparison_operator === "range" && (
              <>
                <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk shrink-0">
                  –
                </span>
                <input
                  type="number"
                  value={condition?.value?.[1] || ""}
                  onChange={(e) => handleNumberInputChange(e.target.value, 1)}
                  onKeyDown={(e) => {
                    if (["e", "E", "+"].includes(e.key)) e.preventDefault();
                  }}
                  placeholder={FILTER_NUMBER_VALUE_PLACEHOLDERS.max}
                  className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </>
            )}
          </div>
        ) : isDealFreeTextField ? (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleTextInputChange(e.target.value)}
              placeholder={valueLoadingPlaceholder}
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors"
            />
          </div>
        ) : isDealArrayAsSingle || isLeadStringAsSingle ? (
          <FilterDropdown
            options={valueOptions}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valueLoadingPlaceholder}
            width="min-w-[150px] max-w-[150px]"
            isLoading={isValueLoading}
            showSearch={valueOptions?.length > 4}
          />
        ) : isDurationField || isSingleValueField ? (
          <FilterDropdown
            options={isDurationField ? DURATION_OPTIONS : valueOptions}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valueLoadingPlaceholder}
            width="min-w-[150px] max-w-[150px]"
            isLoading={isValueLoading}
          />
        ) : (
          <div className="min-w-[150px] max-w-[150px]">
            <MultiSelectSearchableDropdown
              disabled={isValueLoading}
              optionClassName={
                condition?.key === "current_stage"
                  ? "!px-3 !py-1.5 !gap-1.5 !h-[34px]"
                  : ""
              }
              options={
                isValueLoading
                  ? []
                  : valueOptions?.map(
                      (opt: { key: string; label: string }) => ({
                        value: opt?.key,
                        label: opt?.label,
                        selectedOptionContent: opt?.label,
                        content: (
                          <span className="flex items-center gap-1.5 text-md min-w-0 text-verba-indigo dark:text-verba-indigo-dk">
                            <TruncatedTextTooltip text={opt?.label} />
                          </span>
                        ),
                      })
                    )
              }
              value={condition?.value || []}
              onChange={(vals) => handleValueChange(vals)}
              placeholder={
                isValueLoading
                  ? `Loading ${valueLoadingPlaceholder}...`
                  : "Select value"
              }
              showSearch={!isValueLoading && valueOptions?.length > 4}
              showSelectedOptionsChips={true}
              autoFitChips
              moreTagText=""
              chipClassName="inline-flex items-center px-0 py-0 text-md font-medium rounded-none border-none bg-transparent text-heading-text dark:text-heading-text-dk shrink min-w-0 [&>button]:hidden after:content-[','] last:after:content-[''] after:ml-[1.5px]"
              moreTagClassName="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-tag-gray dark:bg-tag-gray-dk text-heading-text dark:text-heading-text-dk shrink-0 font-medium"
              triggerClassName="!rounded-lg h-[32px]"
              inputContainerClassName="!py-0 h-full rounded-lg !bg-pure-white dark:!bg-pure-white-dk"
              placeholderClassName="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk"
              selectedOptionClassName="!bg-card-highlight-2 dark:!bg-card-highlight-2-dk !border-b-selected-option-highlight dark:!border-b-selected-option-highlight-dk"
              dropdownClassName="!max-h-[200px] !min-w-[170px]"
              optionsListClassName="!py-0"
              className="w-full"
            />
          </div>
        ))}

      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 w-[14px] h-[14px] flex items-center justify-center cursor-pointer text-dark-gray dark:text-dark-gray-dk hover:text-verba-red-alert dark:hover:text-verba-red-alert-dk transition-colors"
      >
        <TrashIcon className="w-[14px] h-[14px]" />
      </button>
    </div>
  );
};
